import type { GetToken } from "./canvasService";
import { apiUrl } from "@/config/api";
import { tracedFetch } from "@/utils/tracedFetch";

export interface ApiMcpKey {
  id: string;
  name: string;
  keyPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  expiresAt: string | null;
  revokedAt: string | null;
}

/**
 * A freshly minted key. `token` is the plaintext and exists only in the create
 * response — the server stores a one-way hash, so this value can never be
 * fetched again. It must be shown to the user immediately and never persisted.
 */
export interface ApiMcpKeyCreated extends ApiMcpKey {
  token: string;
}

// Wire types (backend snake_case JSON)
interface WireMcpKey {
  id: string;
  name: string;
  key_prefix: string;
  created_at: string;
  last_used_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
}

interface WireMcpKeyCreated extends WireMcpKey {
  token: string;
}

function mapKey(w: WireMcpKey): ApiMcpKey {
  return {
    id: w.id,
    name: w.name,
    keyPrefix: w.key_prefix,
    createdAt: w.created_at,
    lastUsedAt: w.last_used_at,
    expiresAt: w.expires_at,
    revokedAt: w.revoked_at,
  };
}

async function authedFetch(url: string, getToken: GetToken, init?: RequestInit): Promise<Response> {
  const token = await getToken();
  const res = await tracedFetch(apiUrl(url), {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
  if (!res.ok) {
    const body = await res.text().catch(() => res.statusText);
    throw new Error(`${res.status}: ${body}`);
  }
  return res;
}

export async function listMcpKeys(getToken: GetToken): Promise<ApiMcpKey[]> {
  const res = await authedFetch("/api/mcp-keys", getToken);
  const wire = (await res.json()) as WireMcpKey[];
  return wire.map(mapKey);
}

/** `expiresInDays` omitted or 0 means the key never expires. */
export async function createMcpKey(
  getToken: GetToken,
  name: string,
  expiresInDays?: number,
): Promise<ApiMcpKeyCreated> {
  const res = await authedFetch("/api/mcp-keys", getToken, {
    method: "POST",
    body: JSON.stringify({ name, expires_in_days: expiresInDays ?? 0 }),
  });
  const wire = (await res.json()) as WireMcpKeyCreated;
  return { ...mapKey(wire), token: wire.token };
}

export async function revokeMcpKey(getToken: GetToken, keyId: string): Promise<void> {
  await authedFetch(`/api/mcp-keys/${keyId}`, getToken, { method: "DELETE" });
}
